import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchApplicants } from '../redux/ApplicantSlice';
import { Search, Check, X, Shield, MapPin, Building2,UserCircle2, CheckCircle, Eye } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { usePagination } from '../hooks/usePagination';
import { toast } from 'react-toastify';
import { Pagination } from '../utils/Pagination';
import debounce from 'lodash/debounce';
import { formatDate } from '../utils/Config';


const ApplicantSearch = () => {
  const dispatch = useDispatch();
  const [searchTerm, setSearchTerm] = useState('');
  const { loading } = useSelector((state) => state.applicants);

  // Create a debounced search function
  const debouncedSearch = useCallback(
    debounce((term) => {
      dispatch(fetchApplicants({
        page: 1,
        limit: 15,
        search: term,
        filter: '',
        filter_type: ''
      }));
    }, 500),
    [dispatch]
  );

  // Handle input change
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    debouncedSearch(value);
  };

  // Clear search results when component unmounts
  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  return (
    <div className="relative">
      <div className="relative">
        <Search 
          className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${
            loading ? 'text-blue-500 animate-pulse' : 'text-gray-400'
          }`} 
          size={20} 
        />
        <input
          type="text"
          value={searchTerm}
          onChange={handleSearchChange}
          placeholder="Search applicants..."
          className="pl-10 pr-4 py-2 w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
        />
      </div>
    </div>
  );
};

// Filter Component
const ApplicantFilter = ({ onFilterChange, currentFilter }) => {
  return (
    <div className="flex flex-col sm:flex-row gap-2">
      {['all', 'pending', 'approved','rejected'].map(status => (
        <button
          key={status}
          onClick={() => onFilterChange(status)}
          className={`px-4 py-2 rounded-lg transition ${currentFilter === status
              ? 'bg-blue-500 text-white'
              : 'bg-gray-100 hover:bg-blue-500 hover:text-white'
            }`}
        >
          {status.charAt(0).toUpperCase() + status.slice(1)}
        </button>
      ))}
    </div>
  );
};


const ApplicantTable = ({ applicants, onApprove }) => {
  const navigate = useNavigate();

  const handleView = (id) => {
    navigate(`/dashboard/applicants/${id}`);
  };

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full bg-white rounded-lg overflow-hidden">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Applicant
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            Phone
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
             Date
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Status
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {applicants?.map((applicant) => (
            <tr key={applicant.id} className="hover:bg-gray-50">
              <td className="px-6 py-4">
                <div className="flex items-center">
                  <div className="flex-shrink-0 h-10 w-10">
                    {applicant?.profile_img ? (
                      <img
                        src={applicant.profile_img}
                        alt=""
                        className="h-10 w-10 rounded-full object-cover"
                      />
                    ) : (
                      <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                        <UserCircle2 className="h-6 w-6 text-gray-400" />
                      </div>
                    )}
                  </div>
                  <div className="ml-4">
                    <div className="text-sm font-medium text-gray-900">
                      {`${applicant?.first_name} ${applicant?.last_name}`}
                    </div>
                    <div className="text-sm text-gray-500 line-clamp-1">
                      {applicant?.email}
                    </div>
                  </div>
                </div>
              </td>
              <td className="px-6 py-4">
                <div className="text-sm text-gray-900">{applicant?.primary_phone|| '-'}</div>
              </td>
              <td className="px-6 py-4">
                <div className="text-sm text-gray-900">{formatDate(applicant?.created_at) || '-'}</div>
              </td>
              <td className="px-6 py-4">
                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full 
                  ${applicant?.status === 'pending' 
                    ? 'bg-yellow-100 text-yellow-800' : applicant?.status === 'approved' ? 'bg-green-100 text-green-800'
                    : 'bg-red-100 text-red-800'}`}
                >
                  {applicant?.status || 'Status'}
                </span>
              </td>
              <td className="px-6 py-4">
                <div className="">
                  <button
                    onClick={() => handleView(applicant.id)}
                    className="text-blue-600 hover:text-blue-900 hover:underline flex gap-1 items-center justify-center"
                    title="View Details"
                  >
                      {/* <Eye className="" /> */}
                    <p>view</p>
                  </button>
                  {/* <button
                    className="text-green-600 hover:text-green-900"
                    title="Approve Applicant"
                  >
                    <CheckCircle className="h-5 w-5" />
                  </button> */}
                  
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// Main Applicants Page
const Applicants = () => {
  const dispatch = useDispatch();
  const applicants = useSelector(state => state.applicants.items);
  const {
    loading,
    error,
    meta
  } = useSelector((state) => state.applicants);

  const {
    pagination,
    handlePageChange,
    updatePaginationData,
    getPageNumbers,
    getItemRange
  } = usePagination({
    initialPage: meta.current_page || 1,
    initialPerPage: meta.per_page || 15
  });

  const [currentFilter, setCurrentFilter] = useState('all');

  useEffect(() => {
    handleFetchApplicants();
  }, [pagination.currentPage, currentFilter]); // Add currentFilter to dependencies

  const handleFetchApplicants = () => {
    const filterParams = currentFilter !== 'all'
      ? { filter: currentFilter, filter_type: 'status' }
      : {};

    dispatch(fetchApplicants({
      page: pagination.currentPage,
      limit: pagination.perPage,
      ...filterParams
    }))
      .unwrap()
      .then(response => {
        if (response.meta) {
          updatePaginationData({
            current_page: response.meta.current_page,
            last_page: response.meta.last_page,
            per_page: response.meta.per_page,
            total: response.meta.total
          });
        }
      })
      .catch((error) => {
        toast.error(error.message || 'Failed to fetch applicants');
      });
  };

  const handleFilterChange = (newFilter) => {
    setCurrentFilter(newFilter);
    handlePageChange(1);
  };


  return (
    <div className="">
      <div className="flex flex-col gap-2 sm:flex-row justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold">Applicants</h1>
        <ApplicantSearch />
      </div>

      <ApplicantFilter
        onFilterChange={handleFilterChange}
        currentFilter={currentFilter}
      />

      {/* {loading ? (
        <div className="text-center py-4">Loading...</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
          {applicants.map(applicant => (
            <ApplicantCard key={applicant.id} applicant={applicant} />
          ))}

        </div>
      )} */}
      {loading ? (
 
      <div className="flex justify-center items-center h-[80vh]">
          <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-blue-500"></div>
      </div>
 ) : (
       <div className='my-6'>
         <ApplicantTable 
          applicants={applicants} 
          // onApprove={handleApprove}
        />
       </div>
      )}
       {!loading && applicants.length === 0 && (
        <div className="text-center py-4 text-gray-500">
          No applicants found
        </div>
      )}

      <Pagination
        currentPage={pagination.currentPage}
        lastPage={pagination.lastPage}
        total={pagination.total}
        itemRange={getItemRange()}
        pageNumbers={getPageNumbers()}
        onPageChange={handlePageChange}
      />
    </div>
  );
};

export default Applicants;